import { useCallback, useEffect, useRef, useState } from "react";
import useInfiniteScroll from "react-infinite-scroll-hook";

interface UsePaginationProps<QueryParams, DataType> {
  initialParams: QueryParams;
  fetchData: (params: QueryParams, signal: AbortSignal) => Promise<DataType[]>;
  clearDataAction: () => void;
  dependencies?: any[];
  fetchOnMount?: boolean;
  enabled?: boolean;
}

export const usePagination = <
  QueryParams extends { page?: number; perPage?: number },
  DataType,
>({
  initialParams,
  fetchData,
  clearDataAction,
  dependencies = [],
  fetchOnMount = true,
  enabled = true,
}: UsePaginationProps<QueryParams, DataType>) => {
  const [query, setQuery] = useState<QueryParams>(initialParams);
  const [data, setData] = useState<DataType[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [pending, setPending] = useState<boolean>(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isFirstRender = useRef<boolean>(true);
  const requestIdRef = useRef<number>(0);

  const initialParamsRef = useRef(initialParams);
  const fetchDataRef = useRef(fetchData);
  const clearDataActionRef = useRef(clearDataAction);
  const enabledRef = useRef(enabled);

  initialParamsRef.current = initialParams;
  fetchDataRef.current = fetchData;
  clearDataActionRef.current = clearDataAction;
  enabledRef.current = enabled;

  const fetchPage = useCallback(async (queryVal: QueryParams) => {
    if (!enabledRef.current) {
      return;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const currentRequestId = ++requestIdRef.current;
    abortControllerRef.current = new AbortController();
    setPending(true);

    try {
      const res = await fetchDataRef.current(
        queryVal,
        abortControllerRef.current.signal,
      );

      if (!Array.isArray(res)) {
        throw new Error("Expected fetchData to return an array");
      }

      if (currentRequestId === requestIdRef.current) {
        setData((prevData) => [
          ...(Array.isArray(prevData) ? prevData : []),
          ...(Array.isArray(res) ? res : []),
        ]);
        setQuery(queryVal);
        if (res?.length < (queryVal?.perPage || 0)) {
          setHasMore(false);
        }
        setPending(false);
      }
    } catch (error) {
      if (currentRequestId === requestIdRef.current) {
        if (!abortControllerRef.current?.signal.aborted) {
          console.error("Error fetching data:", error);
        } else {
          console.debug("Fetch aborted by the user");
        }
        setPending(false);
      }
    }
  }, []);

  const loadMore = () => {
    if (!pending && hasMore) {
      const newQuery = { ...query, page: (query?.page || 1) + 1 };
      fetchPage(newQuery);
    }
  };

  const loadInitialData = useCallback(
    (newParams: QueryParams) => {
      setData([]);
      setHasMore(true);
      fetchPage({ ...initialParamsRef.current, ...newParams, page: 1 });
    },
    [fetchPage],
  );

  const clearData = useCallback(() => {
    setData([]);
    setQuery(initialParamsRef.current);
    setHasMore(true);
    clearDataActionRef.current();
  }, []);

  const updateData = useCallback(
    (updateFn: (currentData: DataType[]) => DataType[]) => {
      setData((prevData) => updateFn(prevData));
    },
    [],
  );

  // Invalidates any in-flight request: aborts the network call and bumps the
  // request id so a response that already resolved is ignored too. For
  // consumers that swap to a data source outside this hook (and would
  // otherwise receive a stale page appended to it).
  const cancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    requestIdRef.current++;
    setPending(false);
  }, []);

  const [sentryRef] = useInfiniteScroll({
    loading: pending,
    hasNextPage: hasMore,
    onLoadMore: loadMore,
    rootMargin: "0px 0px 400px 0px",
  });

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      if (fetchOnMount) {
        fetchPage(initialParamsRef.current);
      }
    } else {
      fetchPage(initialParamsRef.current);
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      clearData();
    };
  }, dependencies);

  return {
    data,
    pending,
    hasMore,
    sentryRef,
    updateData,
    loadInitialData,
    clearData,
    cancel,
  };
};
